import { LightingModeType, type Maybe, TransactionState } from "types";
import { AppState } from "types/mainTypes";

export type ConvergenceInput = {
	appState: AppState;
	hasTransaction: boolean;
	transactionState: Maybe<TransactionState>;
	// True from the moment a transaction reaches Finished until the customer leaves (idle) or a new
	// transaction starts — even after the transaction object is cleared. Computed by the converger.
	inPostOrderHandoff: boolean;
	openDetailDishTypeId: string | null;
	allergens: string[];
	filteredDishTypeIds: readonly string[];
};

export type DesiredFridgeLighting =
	| { kind: "be-authoritative" }
	| { kind: "fe-driven"; mode: LightingModeType; spotlightDishTypeIds: readonly string[] };

// Precedence: Finished/post-order-handoff > (IDLE && !hasTransaction) > allergen-filter > detail-open > default Active.
export const computeDesiredLighting = (input: ConvergenceInput): DesiredFridgeLighting => {
	const {
		transactionState,
		appState,
		hasTransaction,
		inPostOrderHandoff,
		allergens,
		filteredDishTypeIds,
		openDetailDishTypeId,
	} = input;

	// Defer to the BE during Finished AND the post-order hand-off — the one window where the BE
	// force-StandBys and fighting it produced the STAND_BY↔ACTIVE command war. Everywhere else the FE
	// drives the lights (the BE permits Active + spotlights during active browse, with or without a
	// transaction), so closing a detail returns to Active [] and clears the spotlight.
	if (transactionState === TransactionState.Finished || inPostOrderHandoff) {
		return { kind: "be-authoritative" };
	}

	if (appState === AppState.IDLE && !hasTransaction) {
		return { kind: "fe-driven", mode: LightingModeType.StandBy, spotlightDishTypeIds: [] };
	}

	if (allergens.length > 0 && transactionState !== TransactionState.StockCheck) {
		if (openDetailDishTypeId !== null && filteredDishTypeIds.includes(openDetailDishTypeId)) {
			return {
				kind: "fe-driven",
				mode: LightingModeType.Active,
				spotlightDishTypeIds: [openDetailDishTypeId],
			};
		}

		return {
			kind: "fe-driven",
			mode: LightingModeType.Active,
			spotlightDishTypeIds: filteredDishTypeIds,
		};
	}

	if (openDetailDishTypeId !== null) {
		return {
			kind: "fe-driven",
			mode: LightingModeType.Active,
			spotlightDishTypeIds: [openDetailDishTypeId],
		};
	}

	// Default Active with no spotlight — active browse (with or without a transaction) and closing a
	// detail. Returning here (instead of deferring) is what clears a previous spotlight back to all-default.
	return { kind: "fe-driven", mode: LightingModeType.Active, spotlightDishTypeIds: [] };
};
